/**
 * The worked-example corridor: `public/example/NB-Green-Wave.csv`, exactly
 * as shipped, plus the numbers derived from it. Every figure quoted on the
 * learn pages is computed here from the CSV values, never typed in.
 */

export type ExampleDirection = 'NS' | 'N' | 'S' | 'NA';

export interface ExamplePhase {
  id: string;
  duration: number;
  direction: ExampleDirection;
}

export interface ExampleIntersection {
  no: number;
  name: string;
  /** Distance to the next intersection north, metres (0 at the last one) */
  distanceToNext: number;
  speedNorth: number;
  speedSouth: number;
  offset: number;
  phases: ExamplePhase[];
}

const PHASE_PATTERN: Array<[string, number, ExampleDirection]> = [
  ['A', 40, 'NS'],
  ['B', 22, 'N'],
  ['C', 22, 'S'],
  ['D', 18, 'NA'],
  ['E', 18, 'NS'],
];

function phases(): ExamplePhase[] {
  return PHASE_PATTERN.map(([id, duration, direction]) => ({ id, duration, direction }));
}

/** Rows of NB-Green-Wave.csv (No., Name, Dist N, V N, V S, OS, A–E, Dir) */
export const EXAMPLE_CORRIDOR: ExampleIntersection[] = [
  { no: 1, name: 'Riverside Dr & Oak St', distanceToNext: 350, speedNorth: 60, speedSouth: 55, offset: 0, phases: phases() },
  { no: 2, name: 'Riverside Dr & Elm Ave', distanceToNext: 400, speedNorth: 60, speedSouth: 55, offset: 21, phases: phases() },
  { no: 3, name: 'Riverside Dr & Pine Rd', distanceToNext: 300, speedNorth: 60, speedSouth: 55, offset: 45, phases: phases() },
  { no: 4, name: 'Riverside Dr & Cedar Ln', distanceToNext: 450, speedNorth: 60, speedSouth: 55, offset: 63, phases: phases() },
  { no: 5, name: 'Riverside Dr & Birch Way', distanceToNext: 350, speedNorth: 60, speedSouth: 55, offset: 90, phases: phases() },
  { no: 6, name: 'Riverside Dr & Maple Ct', distanceToNext: 0, speedNorth: 60, speedSouth: 55, offset: 111, phases: phases() },
];

export const EXAMPLE_CSV_NAME = 'NB-Green-Wave.csv';
export const EXAMPLE_CSV_PATH = '/example/NB-Green-Wave.csv';

/** Amber deducted at the end of each green run (tool default, seconds) */
export const AMBER_S = 4;

export function kmhToMs(kmh: number): number {
  return (kmh * 1000) / 3600;
}

/** Travel time in seconds over `distance` metres at `kmh` */
export function travelTime(distance: number, kmh: number): number {
  return distance / kmhToMs(kmh);
}

export function cycleLength(intersection: ExampleIntersection): number {
  return intersection.phases.reduce((sum, p) => sum + p.duration, 0);
}

/** Cumulative distance from the first intersection, metres */
export function cumulativeDistance(list: ExampleIntersection[]): number[] {
  const out: number[] = [];
  let d = 0;
  for (const i of list) {
    out.push(d);
    d += i.distanceToNext;
  }
  return out;
}

export interface OffsetStep {
  no: number;
  name: string;
  spacing: number;
  speed: number;
  travel: number;
  cumulative: number;
  csvOffset: number;
}

/** The offset derivation table used on the green-wave and worked-example pages */
export function offsetSteps(list: ExampleIntersection[]): OffsetStep[] {
  const steps: OffsetStep[] = [];
  let cumulative = 0;
  list.forEach((i, index) => {
    const prev = index > 0 ? list[index - 1] : null;
    const travel = prev ? travelTime(prev.distanceToNext, prev.speedNorth) : 0;
    cumulative += travel;
    steps.push({
      no: i.no,
      name: i.name,
      spacing: prev ? prev.distanceToNext : 0,
      speed: prev ? prev.speedNorth : i.speedNorth,
      travel,
      cumulative,
      csvOffset: i.offset,
    });
  });
  return steps;
}

export interface GreenWindow {
  start: number;
  end: number;
}

/** Green windows (within one cycle, relative to the intersection's own cycle start) for a direction */
export function greenWindows(intersection: ExampleIntersection, direction: 'N' | 'S', amber = AMBER_S): GreenWindow[] {
  const matches = (p: ExamplePhase) => p.direction === 'NS' || p.direction === direction;
  const out: GreenWindow[] = [];
  let t = 0;
  let i = 0;
  const ph = intersection.phases;
  while (i < ph.length) {
    if (!matches(ph[i])) {
      t += ph[i].duration;
      i += 1;
      continue;
    }
    const start = t;
    while (i < ph.length && matches(ph[i])) {
      t += ph[i].duration;
      i += 1;
    }
    const end = t - amber;
    if (end > start) out.push({ start, end });
  }
  return out;
}

export function corridorLength(list: ExampleIntersection[]): number {
  return list.slice(0, -1).reduce((sum, i) => sum + i.distanceToNext, 0);
}

export function fmt(n: number, digits = 0): string {
  return n.toFixed(digits);
}

export interface ArrivalRow {
  no: number;
  name: string;
  /** Arrival time at this intersection, corridor clock, seconds */
  arrival: number;
  /** Arrival relative to this intersection's own cycle start */
  inCycle: number;
  /** Whether that instant is green for the travelling direction */
  green: boolean;
  /** The green windows of this intersection for that direction, cycle-relative */
  windows: GreenWindow[];
}

/**
 * Follow one vehicle that leaves the first (northbound) or last (southbound)
 * intersection at the start of its first green for that direction, travelling
 * at each link's design speed, and record whether it meets green downstream.
 */
export function arrivalCheck(list: ExampleIntersection[], direction: 'N' | 'S', departOffsetIntoGreen = 0): ArrivalRow[] {
  const order = direction === 'N' ? list : [...list].reverse();
  const cycle = cycleLength(list[0]);
  const first = order[0];
  const firstWindow = greenWindows(first, direction)[0];
  let clock = first.offset + (firstWindow?.start ?? 0) + departOffsetIntoGreen;
  const rows: ArrivalRow[] = [];
  order.forEach((int, i) => {
    if (i > 0) {
      const prev = order[i - 1];
      // Northbound uses prev.distanceToNext; southbound travels the link that int.distanceToNext describes
      const link = direction === 'N' ? prev.distanceToNext : int.distanceToNext;
      const speed = direction === 'N' ? prev.speedNorth : prev.speedSouth;
      clock += travelTime(link, speed);
    }
    const windows = greenWindows(int, direction);
    const inCycle = ((clock - int.offset) % cycle + cycle) % cycle;
    const green = windows.some((w) => inCycle >= w.start && inCycle <= w.end);
    rows.push({ no: int.no, name: int.name, arrival: clock, inCycle, green, windows });
  });
  return rows;
}

/** Width of the northbound band that clears every intersection, seconds (scan at 0.5 s) */
export function bandwidth(list: ExampleIntersection[], direction: 'N' | 'S'): number {
  const order = direction === 'N' ? list : [...list].reverse();
  const cycle = cycleLength(list[0]);
  const first = order[0];
  const firstWindows = greenWindows(first, direction);
  let best = 0;
  let run = 0;
  for (let t = 0; t < cycle * 2; t += 0.5) {
    const departIn = ((t - first.offset) % cycle + cycle) % cycle;
    const departGreen = firstWindows.some((w) => departIn >= w.start && departIn <= w.end);
    let ok = departGreen;
    if (ok) {
      let clock = t;
      for (let i = 1; i < order.length && ok; i += 1) {
        const prev = order[i - 1];
        const int = order[i];
        const link = direction === 'N' ? prev.distanceToNext : int.distanceToNext;
        const speed = direction === 'N' ? prev.speedNorth : prev.speedSouth;
        clock += travelTime(link, speed);
        const inCycle = ((clock - int.offset) % cycle + cycle) % cycle;
        ok = greenWindows(int, direction).some((w) => inCycle >= w.start && inCycle <= w.end);
      }
    }
    if (ok) {
      run += 0.5;
      if (run > best) best = run;
    } else {
      run = 0;
    }
  }
  return Math.min(best, cycle);
}
